"use client";

import { useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";

export interface ToolQuotaError {
  gate: "signup" | "limit";
  used: number;
  limit: number;
  message: string;
}

/** What a tool says when the request was refused for size. nginx answers an over-size body
 *  itself (an HTML page, no JSON), so without this every tool fell back to its own
 *  "Failed to merge PDFs" and the person had no idea why. */
export const TOOL_TOO_LARGE =
  "These files are too large. The PDF tools take up to 50 MB in total — try fewer or smaller files.";

export function useToolFetch() {
  const { getAccessToken } = useAuth();
  const [quotaError, setQuotaError] = useState<ToolQuotaError | null>(null);

  const toolFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const token = getAccessToken();
      const headers = new Headers(options.headers);
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      const res = await fetch(url, { ...options, headers });

      // Track tool run event (extract tool slug from URL path)
      if (res.ok) {
        try {
          const slug = url.split("/api/tools/")[1]?.split("?")[0];
          if (slug) {
            window.sfAnalytics?.track("tool_run", "Tool", slug);
          }
        } catch {}
      }

      if (res.status === 413) {
        const body = await res.json().catch(() => null);
        throw new Error(typeof body?.detail === "string" ? body.detail : TOOL_TOO_LARGE);
      }

      if (res.status === 429) {
        try {
          const body = await res.json();
          if (body?.detail?.gate) {
            const detail = body.detail;
            setQuotaError({
              gate: detail.gate,
              used: detail.used,
              limit: detail.limit,
              message: detail.message,
            });
            throw new Error(detail.message);
          }
        } catch (e: any) {
          if (e.message && !e.message.includes("JSON")) throw e;
        }
        throw new Error("Rate limit exceeded. Please try again later.");
      }

      return res;
    },
    [getAccessToken]
  );

  const clearQuotaError = useCallback(() => setQuotaError(null), []);

  return { toolFetch, quotaError, clearQuotaError };
}
